/**
 * 加群面板（对齐「SDK 未封装 joinGroup」）：
 * 1) 输入关键字 → searchGroups（OpenIM WASM SDK）
 * 2) 点「申请加入」→ joinGroup（OpenIM WASM SDK）
 *
 * 刻意不调用 /api/im/*：Nexora 后端只做身份桥（bootstrap + 搜人），
 * 加群属于 OpenIM 数据面，由前端 SDK 直连完成。
 */
import { useState } from "react";
import { Button, Flex, Input, List, message } from "antd";
import type { GroupSearchHit } from "../types";

type Props = {
  /** 离线时禁止搜索/申请 */
  disabled?: boolean;
  /** 搜群：返回可展示的群摘要列表 */
  searchGroups: (keyword: string) => Promise<GroupSearchHit[]>;
  /**
   * 申请入群。
   * 成功仅表示「请求已提交」；是否立刻进群取决于群的 needVerification。
   */
  joinGroup: (groupID: string, reqMsg?: string) => Promise<void>;
};

export function JoinGroupPanel({ disabled, searchGroups, joinGroup }: Props) {
  // 搜索框草稿
  const [q, setQ] = useState("");
  // 最近一次搜索命中
  const [hits, setHits] = useState<GroupSearchHit[]>([]);
  // 搜索请求进行中
  const [loading, setLoading] = useState(false);
  // 正在申请的那个 groupID（按钮 loading 用）
  const [joiningId, setJoiningId] = useState("");

  /** 触发搜群；空关键字直接提示，避免无意义请求 */
  const doSearch = async () => {
    const keyword = q.trim();
    if (!keyword) {
      message.warning("请输入群名或群 ID");
      return;
    }
    setLoading(true);
    try {
      // 结果可能为空：群未开放被搜时属正常
      setHits(await searchGroups(keyword));
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  /** 对某一条命中发起入群申请 */
  const doJoin = async (groupID: string) => {
    setJoiningId(groupID);
    try {
      await joinGroup(groupID, "你好，申请入群");
      message.success("已提交入群申请（若群无需审批会直接进入）");
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setJoiningId("");
    }
  };

  return (
    <Flex vertical gap={8} style={{ height: "62vh" }}>
      {/* 搜索条：Enter 或点按钮都可搜 */}
      <Flex gap={8}>
        <Input
          allowClear
          placeholder="群名称或群 ID"
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          onPressEnter={() => void doSearch()}
        />
        <Button
          type="primary"
          loading={loading}
          disabled={disabled}
          onClick={() => void doSearch()}
        >
          搜索
        </Button>
      </Flex>

      <div style={{ flex: 1, overflow: "auto" }}>
        <List
          loading={loading}
          locale={{ emptyText: "暂无结果" }}
          dataSource={hits}
          renderItem={(g) => (
            <List.Item
              actions={[
                <Button
                  key="join"
                  size="small"
                  type="link"
                  disabled={disabled}
                  // 只给当前正在申请的那一行转圈，避免整表锁死
                  loading={joiningId === g.groupID}
                  onClick={() => void doJoin(g.groupID)}
                >
                  申请加入
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={g.groupName}
                description={`${g.groupID} · ${g.memberCount} 人`}
              />
            </List.Item>
          )}
        />
      </div>
    </Flex>
  );
}
